import {
  CHECKOUT_RECEIVE_PAYMENTS,
  NEW_PAYMENT_PRIMARY,
  RECEIVE_NEW_PAYMENT_FIELD,
  SET_CHECKOUT_PAYMENTS,
  SET_CHECKOUT_PAYMENTS_LOADING,
  VALIDATE_PAYMENT_FIELDS
} from 'constants/reduxActions';
import { CHECKOUT_STEP_MAP, PAYMENT_STEP, TBD_STEP } from 'constants/checkoutFlow';

import { isDigitalCart, isDigitalChallenge } from 'helpers/CheckoutUtils';
import { processHeadersMiddleware } from 'middleware/processHeadersMiddlewareFactory';
import { setSessionCookies } from 'actions/session';
import {
  changeAddress,
  checkoutData,
  creditCard,
  deleteAddress,
  paymentTypes,
  placeOrder,
  primaryPayment,
  savePayment
} from 'apis/mafia';
import {
  addNewAddress,
  newAddressPrimary,
  storeSelectedAddress
} from 'actions/checkout/addresses';
import {
  fetchApiNotAuthenticatedMiddleware,
  fetchErrorMiddleware,
  fetchMafiaValidationErrorMiddleware
} from 'middleware/fetchErrorMiddleware';
import {
  catchHandlerFactory,
  fetchCheckoutData,
  hasFieldErrors,
  setCheckoutConfigureIsProcessing,
  setConfirmCardMessage,
  setUsePromoBalance,
  updateCheckoutForTargetStep
} from 'actions/checkout/purchase';

export function defaultEndpoints() {
  return {
    changeAddress,
    creditCard,
    deleteAddress,
    savePayment,
    checkoutData,
    paymentTypes,
    placeOrder,
    primaryPayment
  };
}

export function receiveCheckoutConfigurePayments(data) {
  return { type: SET_CHECKOUT_PAYMENTS, data };
}

export function receiveNewPaymentField(fieldKey, fieldValue) {
  return { type: RECEIVE_NEW_PAYMENT_FIELD, fieldKey, fieldValue };
}

export function newPaymentPrimary(isNewPrimary) {
  return { type: NEW_PAYMENT_PRIMARY, isNewPrimary };
}

export function validatePaymentFields() {
  return { type: VALIDATE_PAYMENT_FIELDS };
}

export function makePaymentPrimary({ name, paymentInstrumentId }, mafiaEndpoints = defaultEndpoints()) {
  return function (dispatch, getState) {
    const { environmentConfig: { api: { mafia } } } = getState();

    return mafiaEndpoints.primaryPayment(mafia, name, paymentInstrumentId)
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware);
  };
}

export function fetchPayments(mafiaEndpoints = defaultEndpoints()) {
  return function (dispatch, getState) {
    const {
      cookies,
      checkoutData: {
        purchaseData: {
          data: { shippingAddressId }
        }
      },
      environmentConfig: { api: { mafia } }
    } = getState();

    dispatch(setPaymentsAreLoadingMessage('Loading your payment information...'));
    return mafiaEndpoints.paymentTypes({ ...mafia, shippingAddressId }, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(response => {
        dispatch(receiveCheckoutPayments(response));
      })
      .catch(catchHandlerFactory(dispatch, '/checkout'));
  };
}

export function receiveCheckoutPayments(data) {
  return { type: CHECKOUT_RECEIVE_PAYMENTS, data };
}

export function clearSelectedPayment() {
  return function (dispatch) {
    dispatch(receiveNewPaymentField('cc', ''));
    dispatch(receiveNewPaymentField('expiration', ''));
    dispatch(receiveNewPaymentField('expirationMonth', ''));
    dispatch(receiveNewPaymentField('expirationYear', ''));
    dispatch(receiveNewPaymentField('name', ''));
    dispatch(receiveNewPaymentField('paymentInstrumentId', ''));
    dispatch(receiveNewPaymentField('errors', {}));
  };
}

export function verifyCreditCard(data = {}, mafiaEndpoints = defaultEndpoints()) {
  return function (dispatch, getState) {
    const { number, paymentInstrumentId, addressId } = data;
    const {
      checkoutData: {
        purchaseData: {
          constraintViolations: cvs,
          data: { purchaseId }
        }
      },
      environmentConfig: { api: { mafia } }
    } = getState();

    // if there is an address challenge that is type DigitalChallenge,
    // we must pass the purchaseId, otherwise pass null for purchaseId for faster
    // api call
    const isADigitalChallenge = isDigitalChallenge(cvs);
    const purchaseIdOnDigitalChallenge = isADigitalChallenge ? purchaseId : null;

    dispatch(setCheckoutConfigureIsProcessing(true));
    return mafiaEndpoints.creditCard(mafia, number, paymentInstrumentId, addressId, purchaseIdOnDigitalChallenge)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(response => {
        const { status } = response;
        if (!status) {
          dispatch(setCheckoutConfigureIsProcessing(false));
          dispatch(setConfirmCardMessage('Credit card number doesn\'t match. Please try entering it again.'));
        } else {
          // caap goes back to select payment screen after confirming while this will
          // use the card they just confirmed.  This is a slight variation from caap
          // that will save the user an extra click of having to "continue" with the
          // card they just confirmed.  If we don't want this behavior, we'd have to pass
          // a param here to identify where to go next after checkoutConfigure completes
          // as the reducer will keep things moving to the next available section at this time
          const paymentMethods = [{
            paymentInstrumentId: paymentInstrumentId,
            paymentMethodCode: 'CC'
          }];

          return dispatch(fetchCheckoutData({ advanceOnSuccess: true, fetchAddressAssociations: true, paymentMethods, showFullPageSpinner: false }));
        }
      })
      .catch(catchHandlerFactory(dispatch, CHECKOUT_STEP_MAP[TBD_STEP]));
  };
}

export function updatePaymentAddress({ billingAddressId, advanceOnSuccess = true, mafiaEndpoints = defaultEndpoints() } = {}) {
  return function (dispatch, getState) {
    const {
      checkoutData: { purchaseData: {
        cartType,
        data: { purchaseId },
        purchaseCreditCard = {}
      } },
      environmentConfig: { api: { mafia } }
    } = getState();

    dispatch(setCheckoutConfigureIsProcessing(true));
    // TBD: also store that our payment is processing too (for fancier desktop display)

    const isADigitalCart = isDigitalCart(cartType);
    const purchaseIdOnDigitalCart = isADigitalCart ? purchaseId : null;
    const params = {
      instrument: purchaseCreditCard,
      addressId: billingAddressId,
      purchaseId: purchaseIdOnDigitalCart
    };

    return mafiaEndpoints.savePayment(mafia, params)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(() => {
        const { paymentInstrumentId } = purchaseCreditCard;
        const fetchParams = {
          advanceOnSuccess,
          includePaymentsAndAddresses: true,
          showFullPageSpinner: false,
          paymentMethods: [{
            paymentInstrumentId,
            paymentMethodCode: 'CC'
          }]
        };

        return dispatch(fetchCheckoutData(fetchParams, mafiaEndpoints));
      })
      .catch(catchHandlerFactory(dispatch, CHECKOUT_STEP_MAP[TBD_STEP]));
  };
}

export function addNewPayment(mafiaEndpoints = defaultEndpoints()) {
  return function (dispatch, getState) {
    dispatch(validatePaymentFields());
    const {
      checkoutData: {
        addresses: { saved: savedAddresses },
        purchaseData: {
          cartType,
          data: { purchaseId, shippingAddressId },
          doesPurchaseRequireCC,
          hasSeenIsAlsoBilling,
          isAlsoBilling
        },
        paymentTypes
      },
      environmentConfig: { api: { mafia } }
    } = getState();

    // this is where we should surface errors about empty payment type fields
    if (!paymentTypes.new || (paymentTypes.new.errors && hasFieldErrors(paymentTypes.new.errors))) {
      return;
    }

    // 0 = no billing address on payment
    const addressId = hasSeenIsAlsoBilling && isAlsoBilling ? shippingAddressId : 0;
    const isNewPrimary = !!paymentTypes.isNewPrimary;

    dispatch(setCheckoutConfigureIsProcessing(true));

    const isADigitalCart = isDigitalCart(cartType);
    const purchaseIdOnDigitalCart = isADigitalCart ? purchaseId : null;

    const params = {
      instrument: paymentTypes.new,
      addressId,
      purchaseId: purchaseIdOnDigitalCart
    };

    return mafiaEndpoints.savePayment(mafia, params)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchMafiaValidationErrorMiddleware)
      .then(fetchErrorMiddleware)
      .then(response => {
        const paymentInstrumentId = paymentTypes.new.paymentInstrumentId || response.paymentInstrumentId;
        if (!doesPurchaseRequireCC) {
          // if there is a promo balance covering the purchase, and someone adds a new CC, we
          // can assume that they desire to use the CC on the purchase
          dispatch(setUsePromoBalance(false));
        }

        dispatch(fetchCheckoutData({
          includePaymentsAndAddresses: true,
          paymentMethods: [{
            paymentInstrumentId: paymentInstrumentId,
            paymentMethodCode: 'CC'
          }],
          showFullPageSpinner: false,
          advanceOnSuccess: true
        }, mafiaEndpoints));

        if (isNewPrimary) {
          // before we can set it as primary, set the shipping address as primary if there isn't
          // already a primary shipping address, else this will blow up
          const primaryAddress = (savedAddresses || []).find(
            item => item.isDefaultShippingAddress);

          if (!primaryAddress) {
            dispatch(storeSelectedAddress(shippingAddressId));
            dispatch(newAddressPrimary(true));
            dispatch(addNewAddress({ name: paymentTypes.new.name, paymentInstrumentId }, mafiaEndpoints));
          } else {
            dispatch(makePaymentPrimary({ name: paymentTypes.new.name, paymentInstrumentId }, mafiaEndpoints));
          }
        }

      })
      .catch(catchHandlerFactory(dispatch, CHECKOUT_STEP_MAP[TBD_STEP]));
  };
}

export function defaultSelectedPayment() {
  return function (dispatch, getState) {
    const {
      checkoutData: {
        purchaseData: {
          doesPurchaseRequireCC,
          purchaseCreditCard,
          usePromoBalance
        },
        paymentTypes
      }
    } = getState();

    const { saved: savedPayments } = paymentTypes;

    let paymentInstrumentId;

    if (!doesPurchaseRequireCC && (usePromoBalance || !(savedPayments && savedPayments.length))) {
      paymentInstrumentId = 'savedBalance';
    } else if (purchaseCreditCard) {
      ({ paymentInstrumentId } = purchaseCreditCard);
    } else if (savedPayments && savedPayments.length) {
      const doesNotRequireAssoc = (savedPayments || []).find(
        item => item.associatedAddress && item.associatedAddress.associated) || {};
      paymentInstrumentId = doesNotRequireAssoc.paymentInstrumentId || savedPayments[0].paymentInstrumentId;
    }

    if (!paymentInstrumentId) {
      return;
    }

    const selectedPayment = (savedPayments || []).find(
      item => item.paymentInstrumentId === paymentInstrumentId) || { expirationMonth: '', expirationYear: '', fullName: '' };

    dispatch(receiveNewPaymentField('expiration', `${selectedPayment.expirationMonth} ${selectedPayment.expirationYear}`));
    dispatch(receiveNewPaymentField('expirationMonth', selectedPayment.expirationMonth));
    dispatch(receiveNewPaymentField('expirationYear', selectedPayment.expirationYear));
    dispatch(receiveNewPaymentField('name', selectedPayment.fullName));
    dispatch(receiveNewPaymentField('paymentInstrumentId', paymentInstrumentId));
  };
}

export function setPaymentsAreLoadingMessage(loadingMessage) {
  return { type: SET_CHECKOUT_PAYMENTS_LOADING, loadingMessage };
}

export function goToPaymentStep() {
  return function (dispatch) {
    dispatch(updateCheckoutForTargetStep({ asRedirect: true, targetStep: PAYMENT_STEP, newUrl: CHECKOUT_STEP_MAP[PAYMENT_STEP] }));
  };
}
